import { useCallback, useEffect, useRef, useState } from 'react';
import type { TransferProgress } from '@web/vin-sessions/utils/upload-transfer';

const SLOW_SEND_MS = 5_000;

export const useFileSend = (isSending: boolean, slowSendMs = SLOW_SEND_MS) => {
    const [progress, setProgress] = useState<TransferProgress>();
    const [showProcessingNotice, setShowProcessingNotice] = useState(false);
    const controllerRef = useRef<AbortController | null>(null);
    const startedAtRef = useRef(0);

    useEffect(() => {
        if (!isSending) {
            controllerRef.current = null;
            setProgress(undefined);
        }
    }, [isSending]);

    const start = useCallback(() => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        startedAtRef.current = Date.now();
        setProgress(undefined);
        return controller.signal;
    }, []);

    const finish = useCallback(() => {
        if (Date.now() - startedAtRef.current > slowSendMs) {
            setShowProcessingNotice(true);
        }
    }, [slowSendMs]);

    const cancel = useCallback(() => controllerRef.current?.abort(), []);

    const dismissProcessingNotice = useCallback(() => setShowProcessingNotice(false), []);

    return { progress, setProgress, start, finish, cancel, showProcessingNotice, dismissProcessingNotice };
};

export const useCloseLeaveWhenSent = (isSending: boolean, isLeaving: boolean, closeLeave: () => void) => {
    const wasSendingRef = useRef(isSending);

    useEffect(() => {
        if (wasSendingRef.current && !isSending && isLeaving) {
            closeLeave();
        }
        wasSendingRef.current = isSending;
    }, [isSending, isLeaving, closeLeave]);
};
